import React, { useEffect, useState } from "react";
import { Plus, Pencil, X, Users } from "lucide-react";

const API = process.env.REACT_APP_API_BASE || "http://127.0.0.1:8000";
const card = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";
const inputCls = "w-full bg-black border border-[#00ff90]/20 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00ff90]/50 transition-colors";
const labelCls = "text-xs text-[#00ff90]/50 uppercase tracking-wider block mb-1.5";

const CALC_TYPES = ["FIXED","PCT_OF_BASIC","PCT_OF_GROSS","PCT_OF_CTC","FORMULA"];
const COMPONENT_TYPES = ["EARNING","DEDUCTION","STATUTORY"];

// The admin payroll list endpoints wrap their payload in a single named key
// (GET /salary-components -> {"components": [...]}), so unwrap by key and fall
// back to an empty list rather than letting a shape surprise reach .map().
function toList(payload, key) {
  if (Array.isArray(payload)) return payload;
  if (payload && Array.isArray(payload[key])) return payload[key];
  return [];
}

function useCrud(apiPath, listKey) {
  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const token = localStorage.getItem("token");

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}${apiPath}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setItems(toList(await res.json(), listKey));
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const save = async (draft, editingId) => {
    const isNew = editingId === "new";
    const res = await fetch(isNew ? `${API}${apiPath}` : `${API}${apiPath}/${editingId}`, {
      method: isNew ? "POST" : "PATCH",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify(draft),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    await load();
  };

  const del = async (id) => {
    await fetch(`${API}${apiPath}/${id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
    setItems(i => i.filter(x => x.id !== id));
  };

  return { items, loading, error, save, del, reload: load };
}

// ---------- Salary Components ----------
function ComponentsPanel() {
  const { items, loading, error, save, del } = useCrud("/api/admin/payroll/salary-components", "components");
  const list = Array.isArray(items) ? items : [];
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const doSave = async () => {
    setSaving(true); setErr(null);
    try { await save(draft, editing); setEditing(null); }
    catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className={card}>
      <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
        <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Salary Components</span>
        <button onClick={() => { setDraft({ component_type: "EARNING", calc_type: "FIXED", value: 0, taxable: true }); setEditing("new"); }}
          className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-[#00ff90]/25 bg-[#00ff90]/8 text-[#00ff90]/70 hover:bg-[#00ff90]/15 hover:text-[#00ff90] transition-colors">
          <Plus size={12} /> Add
        </button>
      </div>

      {editing && (
        <div className="px-5 py-4 border-b border-white/5 bg-[#00ff90]/3 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><label className={labelCls}>Name</label>
              <input className={inputCls} value={draft.name || ""} onChange={e => setDraft(d => ({ ...d, name: e.target.value }))} placeholder="e.g. Basic, HRA, PF" /></div>
            <div><label className={labelCls}>Code</label>
              <input className={inputCls} value={draft.code || ""} onChange={e => setDraft(d => ({ ...d, code: e.target.value }))} placeholder="e.g. BASIC, HRA, PF_EMP" /></div>
            <div><label className={labelCls}>Component Type</label>
              <select className={inputCls} value={draft.component_type || "EARNING"} onChange={e => setDraft(d => ({ ...d, component_type: e.target.value }))}>
                {COMPONENT_TYPES.map(t => <option key={t}>{t}</option>)}
              </select></div>
            <div><label className={labelCls}>Calc Type</label>
              <select className={inputCls} value={draft.calc_type || "FIXED"} onChange={e => setDraft(d => ({ ...d, calc_type: e.target.value }))}>
                {CALC_TYPES.map(t => <option key={t}>{t}</option>)}
              </select></div>
            <div><label className={labelCls}>Value (₹ or %)</label>
              <input type="number" step="0.01" className={inputCls} value={draft.value ?? ""} onChange={e => setDraft(d => ({ ...d, value: parseFloat(e.target.value) }))} /></div>
            <div className="flex items-end gap-4 pb-1">
              <label className="flex items-center gap-2 cursor-pointer text-xs text-white/50">
                <input type="checkbox" checked={!!draft.taxable} onChange={e => setDraft(d => ({ ...d, taxable: e.target.checked }))} className="w-4 h-4 accent-[#00ff90]" />
                Taxable
              </label>
              <label className="flex items-center gap-2 cursor-pointer text-xs text-white/50">
                <input type="checkbox" checked={!!draft.pf_applicable} onChange={e => setDraft(d => ({ ...d, pf_applicable: e.target.checked }))} className="w-4 h-4 accent-[#00ff90]" />
                PF Applicable
              </label>
            </div>
          </div>
          {err && <p className="text-red-400 text-xs">⚠ {err}</p>}
          <div className="flex gap-2 justify-end">
            <button onClick={() => setEditing(null)} className="text-xs px-3 py-1.5 rounded-lg border border-white/10 text-white/40 hover:text-white transition-colors">Cancel</button>
            <button onClick={doSave} disabled={saving}
              className="text-xs px-4 py-1.5 rounded-lg bg-[#00ff90]/15 text-[#00ff90] border border-[#00ff90]/30 hover:bg-[#00ff90]/25 disabled:opacity-40 transition-colors">
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      )}

      {error && <p className="px-5 py-3 text-red-400 text-xs">⚠ {error}</p>}
      <div className="divide-y divide-white/5 max-h-72 overflow-y-auto">
        {loading ? Array.from({length:3}).map((_,i) => <div key={i} className="px-5 py-4"><div className="h-8 rounded-lg bg-white/5 animate-pulse" /></div>)
        : list.length === 0 ? <p className="px-5 py-6 text-white/20 text-sm italic text-center">No components defined.</p>
        : list.map(c => (
          <div key={c.id} className="flex items-center justify-between gap-3 px-5 py-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm text-white/70 font-medium">{c.name}</span>
                <span className="text-xs text-white/25 font-mono">{c.code}</span>
                <span className={`text-xs px-1.5 py-0.5 rounded border ${c.component_type === "EARNING" ? "text-[#00ff90]/60 bg-[#00ff90]/5 border-[#00ff90]/15" : c.component_type === "DEDUCTION" ? "text-red-400/60 bg-red-500/5 border-red-500/15" : "text-blue-400/60 bg-blue-500/5 border-blue-500/15"}`}>
                  {c.component_type}
                </span>
              </div>
              <p className="text-xs text-white/25 mt-0.5">{c.calc_type} · {c.value}{c.calc_type !== "FIXED" ? "%" : "₹"}{c.taxable ? " · Taxable" : ""}{c.pf_applicable ? " · PF" : ""}</p>
            </div>
            <div className="flex gap-1.5 shrink-0">
              <button onClick={() => { setDraft({...c}); setEditing(c.id); }} className="w-7 h-7 rounded-lg border border-white/10 bg-white/5 flex items-center justify-center text-white/30 hover:text-[#00ff90] transition-colors"><Pencil size={12} /></button>
              <button onClick={() => del(c.id)} className="w-7 h-7 rounded-lg border border-white/10 bg-white/5 flex items-center justify-center text-white/30 hover:text-red-400 transition-colors"><X size={12} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------- Bulk Revision ----------
function BulkRevisionPanel() {
  const [form, setForm] = useState({ scope: "ALL", pct: 0, reason: "", effective_date: "" });
  const [busy, setBusy] = useState(false);
  const [msg,  setMsg]  = useState(null);
  const token = localStorage.getItem("token");

  const apply = async () => {
    if (!form.reason || !form.effective_date) { setMsg({ type: "error", text: "Reason and effective date required." }); return; }
    setBusy(true); setMsg(null);
    try {
      const res = await fetch(`${API}/api/admin/payroll/bulk-revision`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setMsg({ type: "success", text: `✓ Revision applied to ${data.affected || 0} employees.` });
    } catch (e) { setMsg({ type: "error", text: e.message }); }
    finally { setBusy(false); }
  };

  return (
    <div className={card}>
      <div className="px-5 py-4 border-b border-white/5 flex items-center gap-2">
        <Users size={13} className="text-[#00ff90]/60" />
        <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Bulk Salary Revision</span>
      </div>
      <div className="p-5 space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div><label className={labelCls}>Scope</label>
            <input className={inputCls} value={form.scope} onChange={e => setForm(f => ({...f, scope: e.target.value}))} placeholder="ALL / Engineering / London…" /></div>
          <div><label className={labelCls}>Increment %</label>
            <input type="number" step="0.1" className={inputCls} value={form.pct} onChange={e => setForm(f => ({...f, pct: parseFloat(e.target.value)}))} /></div>
          <div><label className={labelCls}>Effective Date</label>
            <input type="date" className={inputCls} value={form.effective_date} onChange={e => setForm(f => ({...f, effective_date: e.target.value}))} /></div>
          <div><label className={labelCls}>Reason</label>
            <input className={inputCls} value={form.reason} onChange={e => setForm(f => ({...f, reason: e.target.value}))} placeholder="Annual increment 2025" /></div>
        </div>
        {msg && <p className={`text-xs ${msg.type === "error" ? "text-red-400" : "text-[#00ff90]"}`}>{msg.text}</p>}
        <button onClick={apply} disabled={busy}
          className="text-xs px-5 py-2 rounded-lg bg-[#00ff90]/15 text-[#00ff90] border border-[#00ff90]/30 hover:bg-[#00ff90]/25 disabled:opacity-40 transition-colors font-medium">
          {busy ? "Applying…" : "Apply Bulk Revision"}
        </button>
        <p className="text-xs text-white/20">All revisions are audited and effective from the date specified.</p>
      </div>
    </div>
  );
}

export default function SalaryStructureTab() {
  return (
    <div className="space-y-5">
      <ComponentsPanel />
      <BulkRevisionPanel />
    </div>
  );
}